import { observer } from 'mobx-react-lite';
import ProposalSection, { ProposalSectionEditableProps } from '../ProposalSection';
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto';
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { CheckBoxWithLabel } from 'reusable/mui/checkbox-with-label';
import ProposalSectionAcceptanceSignee from './ProposalSectionAcceptanceSignee';
import ProposalSectionAcceptanceStore, { ProposalSectionAcceptanceStoreProvider } from './ProposalSectionAcceptanceStore';
import useStore from 'reusable/mobx-store-utils';
import React from 'react';
import { useProposalsViewProposalStore } from '../../ProposalsViewProposalStore';
import { notNull } from '@gap/utils/utils';
import DateUtils from '@gap/utils/date-utils';
import { useOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore';
import { themeConfig }from '@gap/shared/theme';

type ProposalSectionAcceptanceProps = {
   proposal: ProposalDto,
   sectionProps?: ProposalSectionEditableProps,
}

export default observer(function ProposalSectionAcceptance({ proposal, sectionProps }: ProposalSectionAcceptanceProps) {
   const proposalsViewProposalStore = useProposalsViewProposalStore()
   const orgGuardStore = useOrgGuardStore()
   const proposalSectionAcceptanceStore = useStore((sp) => new ProposalSectionAcceptanceStore(sp), { proposal, proposalsViewProposalStore, orgGuardStore })
   const { acceptanceWindow, dueDate, signees } = proposalSectionAcceptanceStore
   const { numSignaturesRemaining, isProposalAccepted } = proposalsViewProposalStore
   const sectionRadius = { xs: '24px', sm: '48px' }

   React.useEffect(() => {
      proposalSectionAcceptanceStore.fetchTermsOfEngagement()
   }, [proposalSectionAcceptanceStore, proposalSectionAcceptanceStore.step4.termsOfEngagementFilename])

   return <ProposalSectionAcceptanceStoreProvider store={proposalSectionAcceptanceStore}>
      <ProposalSection
         heading="Acceptance"
         {...sectionProps}
         sx={{
            backgroundColor: themeConfig.primary[50],
            borderTopLeftRadius: sectionRadius,
            borderTopRightRadius: sectionRadius,
         }}
      >
         <Stack sx={{ gap: 3 }}>
            <Typography variant="body1" sx={{ 'strong': { whiteSpace: 'pre' } }}>
               {acceptanceWindow
                  ? <>This proposal has a {acceptanceWindow} deadline. </>
                  : dueDate
                     ? <>This proposal has a deadline. </>
                     : undefined}
               {dueDate && <>If it has not been accepted by <strong>{dueDate}</strong>, the proposal may no longer be valid. </>}
               If you have any questions in relation to any part of this proposal please feel free to contact us.
            </Typography>

            <Stack>
               {proposalSectionAcceptanceStore.acceptanceForm.agreeToProposalTerms.bindInput((ip) =>
                  <CheckBoxWithLabel
                     {...ip}
                     label="I have read and agree to the terms of this proposal."
                     align="first-line"
                     resetIndent
                     disabled={!proposalsViewProposalStore.currentSignee || !!proposalsViewProposalStore.currentSignee.agreedAt || proposalsViewProposalStore.isProposalAccepted}
                     {...proposalsViewProposalStore.currentSignee?.agreedAt ? {
                        checked: true,
                     } : {}}
                  />)
               }
               {proposal.step4?.termsOfEngagementFilename && !proposalSectionAcceptanceStore.isServiceUser
                  && proposalSectionAcceptanceStore.acceptanceForm.agreeToTermsOfEngagement.bindInput((ip) =>
                     <CheckBoxWithLabel
                        {...ip}
                        label={<>
                           I have read, understood, and accept the<> </>
                           {proposalSectionAcceptanceStore.toeUrl
                              ? <a
                                 href={proposalSectionAcceptanceStore.toeUrl}
                                 style={{ textDecoration: 'underline' }}
                                 target="_blank"
                              >Terms of Engagement</a>
                              : <>Terms of Engagement</>}
                           <> </>and agree to abide by all requirements to
                           the best of my ability.
                        </>}
                        align="first-line"
                        resetIndent
                        disabled={!proposalsViewProposalStore.currentSignee || !!proposalsViewProposalStore.currentSignee.agreedAt || proposalsViewProposalStore.isProposalAccepted}
                        {...proposalsViewProposalStore.currentSignee?.agreedAt ? {
                           checked: true,
                        } : {}}
                     />)
               }
            </Stack>
         </Stack>

         {signees.map((signee) => <ProposalSectionAcceptanceSignee key={signee.id} {...{
            signee,
            currentUserCanSign: !!signee.isCurrentlyAuthenticatedUser,
            onSigned: proposalSectionAcceptanceStore.signAndAccept,
         }} />)}

         {numSignaturesRemaining > 0 &&
            <Stack sx={{
               flexDirection: 'row',
               alignItems: 'center',
               gap: 1,
               color: themeConfig.primary[600]
            }}>
               <InfoIcon fontSize="medium" />
               <Typography variant="body2">
                  {proposalsViewProposalStore.signaturesRequiredText}
               </Typography>
            </Stack>
         }

         {isProposalAccepted &&
            <Typography
               variant="caption"
               sx={{ color: themeConfig.primary[600] }}
            >This proposal was accepted on {DateUtils.formatDate(new Date(notNull(proposal.acceptedAt)), 'date-full-month')}</Typography>
         }
      </ProposalSection>
   </ProposalSectionAcceptanceStoreProvider>
})
